import type { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import { toast } from '@hivebrite/design-system/Toast';

import { updateRelationshipRequest } from 'hb-react/frontoffice/apps/Mentoring/requests';
import type { UpdateRelationshipRequestParams } from 'hb-react/frontoffice/apps/Mentoring/requests/types';
import type { Program } from 'hb-react/frontoffice/apps/Mentoring/types';
import { getProgramTopicId } from 'hb-react/frontoffice/apps/Mentoring/utils';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { QUERY_KEYS } from 'hb-react/shared/apps/Mentoring/constants';

interface UpdateRelationshipProps {
  program: Pick<Program, 'id' | 'parent_id' | 'parent_type'>;
  relationshipId: number;
  onSuccess?: () => void;
}

export const useUpdateRelationshipRequest = ({
  program,
  relationshipId,
  onSuccess,
}: UpdateRelationshipProps) => {
  const queryClient = useQueryClient();
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
  } = program;

  return useMutation(
    (payload: UpdateRelationshipRequestParams['payload']) =>
      updateRelationshipRequest({
        programId,
        relationshipId,
        payload,
        topicId: getProgramTopicId({ parentId, parentType }),
      }),
    {
      onSuccess: (updatedRelationship) => {
        onSuccess?.();
        queryClient.invalidateQueries([
          ITEMS_QUERY_KEYS.mentoring.items(programId),
        ]);
        queryClient.invalidateQueries([
          ITEMS_QUERY_KEYS.myMentoring.mentors(programId),
        ]);
        queryClient.invalidateQueries([
          ITEMS_QUERY_KEYS.myMentoring.mentees(programId),
        ]);
        queryClient.invalidateQueries([ITEMS_QUERY_KEYS.myMentoring.items]);
        queryClient.setQueryData(
          [QUERY_KEYS.relationship, programId, relationshipId],
          updatedRelationship,
        );
      },
      onError: (error: AxiosError) => {
        if (error.response?.status === 500) {
          toast.error(__('Sorry, an error occurred'));
        }
      },
    },
  );
};
